import React, { useEffect } from 'react';
import {
    useTable,
    useFlexLayout,
    useSortBy,
    useGroupBy,
    useExpanded,
    Row,
    useRowSelect,
    usePagination,
    useGlobalFilter,
    TableState
} from 'react-table';
import { applyQuickFilters, applyRichFilters, Button } from '@brighterion/br-ui';
import { map, filter, get } from 'lodash';
import { matchSorter } from 'match-sorter';
import { useTranslation } from 'react-i18next';
import { RoleService } from 'Services';
import useWindowScrollY from 'Hooks/useWindowScrollY';
import { GLOBAL_EXECUTION_ORDER_VIEW, GLOBAL_RULE_TABLE_VIEW } from 'Common/enums';
import useInitialTableState from 'Hooks/reduxHelperHooks/useInitialTableState';
import clsx from 'clsx';
import THead from './THead';
import TBody from './TBody';
import SplitView from './SplitView';
import TableFunctions from './TableFunctions';
import TableLoading from './TableLoading';
import { createColumns, createInitialState } from './utils';
import { alphanumeric, datetime } from './utils/sortTypes';
import { OurTableProps, ITableFilter } from './types';
import { useSplitView } from './plugin-hooks/useSplitView';
import { useStaticGroupBy } from './plugin-hooks/useStaticGroupBy';
import EmptyRuleGroups from '../GlobalRulesTable/EmptyRuleGroups';

import styles from './index.css';

const Table = <D extends object>({
    dataTestId,
    data,
    columns,
    splitViewContent,
    clickableRow,
    initialState = {},
    icon,
    enablePagination,
    showExpandableSearch,
    showMultiSort,
    showRichFilter,
    showNoDataMsg = true,
    noDataMessage = 'No data found for this table',
    isLoading = false,
    pathToIdOnDataObj = 'id',
    onRowInteractCB,
    conditionalRowClasses,
    manualSplitView,
    updateSplitViewId,
    controlledSplitViewId,
    innerTableHeader,
    quickFilters,
    tableName,
    inactiveRowProps,
    handleEditRow,
    emptyGroupsData,
    handleDeleteRow,
    showRuleDropdown,
    setSelectViewOption,
    selectViewOption,
    selectedRows,
    submitForm,
    rulesGroupData,
    dynamicGroupBy,
    sortOptions
}: OurTableProps<D>) => {
    const { t } = useTranslation();
    const { setPersistedState, persistedState } = useInitialTableState(tableName);
    const tableStateRef = React.useRef<TableState<D> | null>(null);
    const { setScrollY } = useWindowScrollY();
    // the initialState shouldn't really change for the table after mounting.
    const tableInitialState = React.useMemo(
        () => createInitialState({ initialState, data, columns, reduxInitialState: persistedState }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [initialState]
    );

    const tableColumns = React.useMemo(() => {
        return createColumns<D>({ icon, columns, dataTestId });
    }, [icon, columns, dataTestId]);

    const globalFilterFn = React.useMemo(
        () => (gRows: Row<D>[], columnIds: string[], filterValue: ITableFilter) => {
            const gData = map(gRows, 'original');

            const dataAfterQuickFilters = applyQuickFilters(gData, filterValue?.quickFilters ?? [], {
                pathToIdOnDataObj
            });

            const dataAfterSearchFilter = matchSorter(dataAfterQuickFilters, filterValue?.searchBarValue || '', {
                keys: columnIds,
                threshold: matchSorter.rankings.CONTAINS
            });

            const dataAfterRichFilters =
                filterValue.richFilters && filterValue.richFilters.length > 0
                    ? applyRichFilters(dataAfterSearchFilter, filterValue?.richFilters)
                    : dataAfterSearchFilter;
            const dataIdsAfterFilters = map(dataAfterRichFilters, pathToIdOnDataObj);
            const reformattedRows = filter(gRows, (row) => {
                return dataIdsAfterFilters.includes(get(row.original, pathToIdOnDataObj));
            });

            return reformattedRows;
        },
        [pathToIdOnDataObj]
    );
    const disableSort = selectViewOption === GLOBAL_EXECUTION_ORDER_VIEW;

    const {
        getTableProps,
        selectedSplitRow,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        toggleRowSplitView,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        page,
        setGlobalFilter,
        allColumns,
        state,
        setSortBy,
        setGroupBy
    } = useTable<D>(
        {
            columns: tableColumns,
            data,
            initialState: tableInitialState,
            globalFilter: globalFilterFn,
            autoResetGroupBy: false,
            autoResetGlobalFilter: false,
            manualSplitView,
            splitViewRowId: controlledSplitViewId,
            disableSortRemove: true,
            autoResetExpanded: false,
            autoResetSplitView: false,
            autoResetSortBy: false,
            autoResetPage: false,
            disableSortBy: disableSort,
            // must be memoized
            sortTypes: React.useMemo(
                () => ({
                    alphanumeric,
                    datetime
                }),
                []
            )
        },
        useGlobalFilter,
        useGroupBy,
        useSortBy,
        useExpanded,
        usePagination,
        useRowSelect,
        useFlexLayout,
        useSplitView,
        useStaticGroupBy
    );
    tableStateRef.current = state;
    const isGlobalRuleSelectOption = selectViewOption === GLOBAL_RULE_TABLE_VIEW;
    const isExecutionOrderSelectOption = selectViewOption === GLOBAL_EXECUTION_ORDER_VIEW;
    const showLoading =
        (showNoDataMsg && rows.length < 1 && (state.globalFilter?.searchBarValue || !emptyGroupsData?.length)) ||
        isLoading;
    useEffect(() => {
        if (isExecutionOrderSelectOption && sortOptions) {
            setGroupBy([]);
            setSortBy(sortOptions);
        } else if (isGlobalRuleSelectOption && sortOptions) {
            setGroupBy(dynamicGroupBy);
            setSortBy(sortOptions);
        }
    }, [
        selectViewOption,
        setGroupBy,
        dynamicGroupBy,
        isExecutionOrderSelectOption,
        isGlobalRuleSelectOption,
        setSortBy,
        sortOptions
    ]);

    const onRowInteract = (row: Row<D>, event: React.MouseEvent | React.KeyboardEvent) => {
        if (splitViewContent) {
            row.toggleSplitView();
        } else if (onRowInteractCB) {
            onRowInteractCB(row, event);
        }
    };

    const rowInteractProps = clickableRow
        ? {
              onRowInteract
          }
        : undefined;

    React.useEffect(() => {
        if (state.sortBy) {
            setScrollY();
        }
    }, [state.sortBy, setScrollY]);

    React.useEffect(() => {
        if (manualSplitView) {
            updateSplitViewId?.(state.splitViewRowId);
        }
    }, [state.splitViewRowId, manualSplitView, updateSplitViewId]);

    React.useEffect(() => {
        return () => {
            if (tableStateRef.current) {
                setPersistedState({
                    ...tableStateRef.current
                });
            }
        };
    }, [setPersistedState]);

    const tableContainerClassName = clsx(
        'brui_table',
        styles.tableContainer,
        innerTableHeader && styles.noMarginTop,
        splitViewContent && styles.minHeight
    );

    return (
        <>
            <TableFunctions
                state={state}
                setSortBy={setSortBy}
                columns={allColumns}
                showMultiSort={showMultiSort}
                showRichFilter={showRichFilter}
                showExpandableSearch={showExpandableSearch}
                setGlobalFilter={setGlobalFilter}
                enablePagination={enablePagination}
                paginationProps={{
                    pageCount,
                    gotoPage,
                    nextPage,
                    previousPage,
                    setPageSize,
                    pageSize: state.pageSize,
                    pageIndex: state.pageIndex
                }}
                quickFilters={quickFilters}
                showRuleDropdown={showRuleDropdown}
                setSelectViewOption={setSelectViewOption}
                selectViewOption={selectViewOption}
            />
            {innerTableHeader ? <div className={styles.innerTableHeader}>{innerTableHeader}</div> : null}
            <div data-testid={dataTestId} className={tableContainerClassName} {...getTableProps()}>
                {selectedRows && (
                    <div className={styles.messageRow}>
                        <div className={styles.countTextContainer}>{selectedRows} selected</div>

                        {tableName === 'removeTenantTable' && RoleService.hasTenantGroupWriteAccess && (
                            <Button
                                variant='tertiary'
                                className={styles.removeBtnContainer}
                                iconName='delete'
                                size='small'
                                onClick={submitForm}
                                onKeyDown={submitForm}
                            >
                                {t('Remove Tenants from Group')}
                            </Button>
                        )}
                    </div>
                )}
                <THead
                    headerGroups={headerGroups}
                    dataTestId={dataTestId}
                    isExecutionOrderSelectOption={isExecutionOrderSelectOption}
                />
                {showLoading ? (
                    <TableLoading
                        noDataMessage={noDataMessage}
                        searchFilter={state.globalFilter?.searchBarValue ?? ''}
                        isLoading={isLoading}
                    />
                ) : (
                    <>
                        <TBody
                            getTableBodyProps={getTableBodyProps}
                            rows={enablePagination ? page : rows}
                            prepareRow={prepareRow}
                            conditionalRowClasses={conditionalRowClasses}
                            inactiveRowProps={inactiveRowProps}
                            handleEditRow={handleEditRow}
                            tableName={tableName}
                            rulesGroupData={rulesGroupData}
                            {...rowInteractProps}
                        />
                        {state.globalFilter?.quickFilters?.every((flagdata) => !flagdata.data.length) &&
                        !state.globalFilter?.searchBarValue?.length &&
                        !state.globalFilter?.richFilters?.length &&
                        selectViewOption !== GLOBAL_EXECUTION_ORDER_VIEW ? (
                            <EmptyRuleGroups emptyGroupsData={emptyGroupsData} handleEditRow={handleDeleteRow} />
                        ) : null}
                        {splitViewContent && selectedSplitRow && (
                            <SplitView dataTestId={`${dataTestId}-splitview`}>
                                {splitViewContent({
                                    selectedRow: selectedSplitRow,
                                    rows,
                                    toggleRowSelected: toggleRowSplitView
                                })}
                            </SplitView>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default Table;
